import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Home,
  CalendarCheck,
  Users,
  ClipboardList,
  School,
  Mail,
  BookOpen,
  GraduationCap,
  MessageSquare,
  MoreHorizontal,
  FileText,
  CreditCard,
  IndianRupee,
  Ticket,
} from 'lucide-react';
import { isMobileContainer } from '../utils/device';
import { isSchoolObserver } from '../utils/featurePermissions';
import { getVisibleMobileTabs, SCHOOL_ADMIN_MOBILE_TABS } from '../utils/navPermissions';

const ICON_BY_KEY = {
  dashboard: Home,
  attendance: ClipboardList,
  students: Users,
  teachers: Users,
  teacher_attendance: ClipboardList,
  timetable: CalendarCheck,
  more: MoreHorizontal,
  schools: School,
  enquiries: Mail,
  homework: BookOpen,
  children: GraduationCap,
  employees: Users,
  reports: FileText,
  feedback: MessageSquare,
  upgrade: CreditCard,
  subscription: IndianRupee,
  plans: CreditCard,
  salaries: IndianRupee,
  tickets: Ticket,
};

export default function MobileNav({ onOpenMenu, menuOpen = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!isMobileContainer()) return null;

  const observerMode = isSchoolObserver(user);
  const visibleTabs = observerMode ? SCHOOL_ADMIN_MOBILE_TABS : getVisibleMobileTabs(user);

  const isActive = (tab) => {
    if (tab.action === 'openMenu') return menuOpen;
    const path = tab.path;
    if (path === '/dashboard' || path === '/crm/dashboard' || path === '/reception') {
      return location.pathname === '/' || location.pathname === path;
    }
    if (path === '/settings/subscription/upgrade') {
      return location.pathname.startsWith('/settings/subscription');
    }
    if (path === '/crm/feedback') {
      return location.pathname.startsWith('/crm/feedback');
    }
    if (path === '/crm/tickets') {
      return location.pathname.startsWith('/crm/tickets');
    }
    if (path === '/feedback') {
      return location.pathname === '/feedback' || location.pathname.startsWith('/feedback/');
    }
    if (path === '/subscription/plans' || path === '/subscription/analytics') {
      return location.pathname.startsWith('/subscription');
    }
    return location.pathname.startsWith(path);
  };

  const handleTabClick = (tab) => {
    if (tab.action === 'openMenu') {
      onOpenMenu?.();
      return;
    }
    navigate(tab.path);
  };

  return (
    <nav
      className="mobile-bottom-nav fixed bottom-0 left-0 right-0 z-40 max-w-full border-t border-border pl-safe pr-safe"
      style={{ paddingBottom: 'var(--mobile-nav-safe-bottom)' }}
    >
      <div className="flex items-stretch justify-around px-1">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon || ICON_BY_KEY[tab.key] || Home;
          const active = isActive(tab);
          const ariaLabel = tab.ariaLabel || tab.label;
          const compactLabels = visibleTabs.length >= 5;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => handleTabClick(tab)}
              className={`relative flex flex-col items-center justify-center flex-1 min-h-[56px] py-1.5 font-medium transition-colors ${
                compactLabels ? 'text-[10px] sm:text-xs' : 'text-xs'
              } ${active ? 'text-primary' : 'text-muted-foreground'}`}
              aria-label={ariaLabel}
              aria-expanded={tab.action === 'openMenu' ? menuOpen : undefined}
            >
              {active && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 w-11 h-0.5 rounded-full bg-primary" />
              )}
              <span
                className={`flex items-center justify-center w-11 h-8 rounded-full mb-0.5 transition-colors ${
                  active ? 'bg-primary/15' : ''
                }`}
              >
                <Icon className={`h-6 w-6 ${active ? 'stroke-primary' : 'stroke-muted-foreground'}`} />
              </span>
              <span className="leading-tight">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
